import { VisuallyHidden } from '@hagerty/react-components';
import clsx from 'clsx';
import React, { useState } from 'react';

import { useEscKeyListener, useOnClickOutside } from '../../hooks';
import { CloseIcon } from '../Icons';

import styles from './Sidebar.module.scss';

import type { StandardProps } from '../../types/common';

interface SidebarProps extends StandardProps {
  /**
   * Items to display in the sidebar. Typically a list of links.
   */
  items?: React.ReactNode;

  /**
   * Determines whether the sidebar is open or closed
   */
  isOpen?: boolean;

  /**
   * Branding to display at the top of the sidebar
   */
  brand?: React.ReactElement;

  /**
   * Callback function to call when the sidebar is closed
   */
  onClose?: () => void;
}

export const Sidebar = ({
  className,
  style,
  items,
  isOpen = false,
  brand,
  onClose,
  testid = 'sidebar',
  ...props
}: SidebarProps): JSX.Element => {
  const [wrapperRef, setWrapperRef] = useState<HTMLDivElement | null>(null);
  useOnClickOutside({ current: wrapperRef }, onClose);
  useEscKeyListener(isOpen, onClose);

  return (
    <div
      className={clsx(
        styles['hds-sidebar'],
        { [styles.show]: isOpen },
        className
      )}
      style={style}
      data-testid={testid}
      {...props}
    >
      <div className={styles['hds-sidebar__head']}>
        <button type='button' className={styles['hds-sidebar__button_close']}>
          <VisuallyHidden>Close menu</VisuallyHidden>
          <CloseIcon className={styles.icon} />
        </button>
      </div>
      <div ref={setWrapperRef} className={styles['hds-sidebar__body']}>
        <div className={styles['hds-sidebar__brand']}>{brand}</div>
        <div className={styles['hds-sidebar__inner']}>{items}</div>
      </div>
    </div>
  );
};
